import { useState, useEffect } from 'react';
import { Link } from 'wouter';
import { MapPin, Clock, Car, Droplets, ChevronLeft } from 'lucide-react';

interface Shop {
  id: string;
  name: string;
  nameAr: string;
  slug: string;
  address: string;
  addressAr: string;
  phone: string;
  bayCount: number;
  estimatedWashMinutes: number;
  actualAvgWashMinutes: number | null;
  openTime: string;
  closeTime: string;
  isActive: boolean;
  queue?: {
    count: number;
    estimatedWaitMinutes: number;
  };
}

export default function ShopsPage() {
  const [shops, setShops] = useState<Shop[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch('/api/shops')
      .then(res => res.json())
      .then(data => {
        setShops(data.shops || []);
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, []);

  const isOpen = (openTime: string, closeTime: string): boolean => {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const [openH, openM] = openTime.split(':').map(Number);
    const [closeH, closeM] = closeTime.split(':').map(Number);
    const openMinutes = openH * 60 + openM;
    const closeMinutes = closeH * 60 + closeM;
    return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
  };

  return (
    <div className="py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">المغاسل الشريكة</h1>
        <p className="text-gray-500 text-sm mb-8">اعثر على مغسلة قريبة مع أوقات الانتظار المباشرة</p>

        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl p-5 shadow-sm animate-pulse">
                <div className="h-5 bg-gray-200 rounded w-1/3 mb-3" />
                <div className="h-4 bg-gray-100 rounded w-2/3 mb-2" />
                <div className="h-4 bg-gray-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : shops.length === 0 ? (
          <div className="text-center py-12">
            <Car className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">لا توجد مغاسل متاحة حالياً</p>
          </div>
        ) : (
          <div className="space-y-4">
            {shops.map((shop) => {
              const open = isOpen(shop.openTime, shop.closeTime);
              const avgWash = shop.actualAvgWashMinutes || shop.estimatedWashMinutes;
              const queueCount = shop.queue?.count || 0;
              const waitMinutes = shop.queue?.estimatedWaitMinutes || 0;

              return (
                <Link
                  key={shop.id}
                  href={`/shops/${shop.slug}`}
                  className="block bg-white rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow border border-gray-100"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h2 className="text-lg font-bold text-gray-900">
                        {shop.nameAr || shop.name}
                      </h2>
                      <div className="flex items-center gap-1.5 mt-1 text-gray-500 text-sm">
                        <MapPin className="w-3.5 h-3.5" />
                        <span>{shop.addressAr || shop.address}</span>
                      </div>
                    </div>
                    <ChevronLeft className="w-5 h-5 text-gray-400 mt-1" />
                  </div>

                  <div className="flex flex-wrap gap-3">
                    {/* Queue Status */}
                    <div className={`flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-lg ${
                      queueCount === 0
                        ? 'bg-green-50 text-green-700'
                        : waitMinutes > 30
                        ? 'bg-red-50 text-red-700'
                        : 'bg-amber-50 text-amber-700'
                    }`}>
                      <Clock className="w-3.5 h-3.5" />
                      {queueCount === 0
                        ? 'لا انتظار!'
                        : `~${waitMinutes} دقيقة`}
                    </div>

                    {/* Bays */}
                    <div className="flex items-center gap-1.5 text-sm text-gray-500 bg-gray-50 px-2.5 py-1 rounded-lg">
                      <Droplets className="w-3.5 h-3.5" />
                      {shop.bayCount} مكان غسيل
                    </div>

                    {/* Avg Wash Time */}
                    <div className="flex items-center gap-1.5 text-sm text-gray-500 bg-gray-50 px-2.5 py-1 rounded-lg">
                      <Clock className="w-3.5 h-3.5" />
                      {avgWash} د متوسط
                    </div>

                    {/* Open/Closed */}
                    <div className={`flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-lg ${
                      open ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      <div className={`w-2 h-2 rounded-full ${open ? 'bg-green-500' : 'bg-gray-400'}`} />
                      {open ? 'مفتوح' : 'مغلق'}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
